// AI agents as genesis instance data (spark#1087, @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2).
//
// An agent is PROJECT CONTEXT, never a team member: not a Person, not a match candidate, never a
// seat. The optimiser never sees any of this. The list itself is admin-curated instance data in
// genesis (@external-adr GENESIS-0074-ai-agents-are-instance-data-copied-once-from-editable-defaults);
// what a match stores is the small `MatchAgent` snapshot, so a saved match survives an edit or a
// delete of the row it was picked from.

import type { AgentCoreFragment, AgentRowFragment } from "@/graphql/generated/operations";
import type { MatchAgent } from "./design-codec";

/** The full agent: its own page and the Stammdaten editor. */
export type Agent = AgentCoreFragment;
/** An agent as the lists load it (id, name, role, covers); every full Agent is one too. */
export type AgentListRow = AgentRowFragment;

type AgentLocalizedText = { localized?: string | null; german?: string | null; english?: string | null };

/**
 * The text to show, trimmed. genesis resolves `localized` from Accept-Language and falls back to the
 * other language when the reader's is missing, as a CMS does; the two halves are read here only as
 * a guard for a row whose `localized` came back null.
 */
export function agentText(text: AgentLocalizedText | null | undefined): string {
  return (text?.localized || text?.german || text?.english || "").trim();
}

/** The entries of a prose list that have any text, resolved for display. */
export function agentTexts(texts: readonly (AgentLocalizedText | null | undefined)[]): string[] {
  return texts.map(agentText).filter(Boolean);
}

/**
 * Shown text plus the language it is in when that differs from the UI's, so the element can carry
 * `lang` for screen readers. `lang` stays undefined when the text is in the UI language or its
 * language cannot be told (both halves identical, or only `localized` was read).
 */
export function agentProse(
  text: AgentLocalizedText | null | undefined,
  uiLocale: string,
): { text: string; lang?: "de" | "en" } {
  const shown = agentText(text);
  const german = text?.german?.trim() ?? "";
  const english = text?.english?.trim() ?? "";
  const ui = uiLocale.startsWith("de") ? "de" : "en";
  if (ui === "de" && shown && shown === english && shown !== german) return { text: shown, lang: "en" };
  if (ui === "en" && shown && shown === german && shown !== english) return { text: shown, lang: "de" };
  return { text: shown };
}

/** Name comparison key: product names are matched trimmed and case-insensitively. */
export function agentNameKey(name: string): string {
  return name.trim().toLocaleLowerCase();
}

/**
 * Is this genesis row already on the match? By id; and by name only for a snapshot whose id is no
 * live row (an old catalogue slug from before the list moved to genesis, or a row since deleted).
 * A snapshot of a live row is that row and nothing else, so a rename or a second row with the same
 * name never matches it.
 * Deduplication only: the snapshot is not rewritten and keeps its old id.
 */
export function isAlreadyStored(
  agent: AgentListRow,
  stored: readonly MatchAgent[],
  live: ReadonlyMap<string, AgentListRow>,
): boolean {
  const key = agentNameKey(agent.name);
  return stored.some((s) => s.id === agent.id || (!live.has(s.id) && agentNameKey(s.name) === key));
}

/** Genesis row → what the match stores, in the language active when it was picked. */
export function toMatchAgent(agent: AgentListRow): MatchAgent {
  return {
    id: agent.id,
    name: agent.name,
    role: agentText(agent.role),
    covers: agentTexts(agent.covers),
  };
}

/**
 * What to show for a stored agent: the live row in the reader's language when the instance still
 * has it, otherwise the snapshot. A snapshot freezes the language it was recorded in, and an old
 * demo slug id never matches a genesis UUID, so both land on the snapshot.
 */
export function displayAgent(
  stored: MatchAgent,
  live: AgentListRow | null | undefined,
): { name: string; role: string; covers: string[] } {
  if (!live) return { name: stored.name, role: stored.role, covers: stored.covers };
  const { name, role, covers } = toMatchAgent(live);
  return { name, role, covers };
}

/**
 * Agents named in a block of assistant prose.
 *
 * Case-SENSITIVE, whole-word: the names are capitalised product tokens, and a loose match would let
 * "zip file" or "glean" in ordinary prose masquerade as a recommendation.
 *
 * Being named is NOT being recommended: the assistant may name an agent to rule it out, and the
 * caller records it anyway (see AgentPicker). That trade is deliberate and demo-scoped.
 */
export function agentsNamedIn<T extends AgentListRow>(text: string, agents: readonly T[]): T[] {
  if (!text) return [];
  return agents.filter((agent) => {
    const escaped = agent.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|[^\\p{L}])${escaped}([^\\p{L}]|$)`, "u").test(text);
  });
}

/**
 * The Stammdaten tab that edits this list; the picker's empty state links to it, and `/a/:id`
 * adds the agent so the tab opens on that agent's editor.
 */
export function agentsAdminPath(agentId?: string): string {
  const path = "/admin/settings?section=stammdaten&tab=agents";
  return agentId ? `${path}&agentId=${encodeURIComponent(agentId)}` : path;
}

/**
 * The product page as a link target, or null when it must not be one. genesis stores the url as
 * entered and accepts a scheme-less value ("ziphq.com") as if it were https; used verbatim as an
 * href it would become a relative link inside spark, so it gets `https://` here. Anything with a
 * scheme other than http(s) is never linked.
 */
export function agentHref(url: string | null | undefined): string | null {
  const value = url?.trim();
  if (!value) return null;
  const colon = value.indexOf(":");
  const slash = value.indexOf("/");
  const scheme = colon > 0 && (slash < 0 || colon < slash) ? value.slice(0, colon).toLowerCase() : null;
  if (scheme === null) return `https://${value.replace(/^\/+/, "")}`;
  return scheme === "http" || scheme === "https" ? value : null;
}

/** An agent's detail view is its own page. */
export function agentPath(agentId: string): string {
  return `/a/${encodeURIComponent(agentId)}`;
}
